import React, { useState } from 'react';

/**
 * BackupCodesDisplay
 * Props:
 *   codes: string[]   — formatted (e.g., "ABCDE-FGHJK")
 *   onAcknowledge(): void  — controller advances to FactorList after user confirms
 */
export default function BackupCodesDisplay({ codes, onAcknowledge }) {
  const [acknowledged, setAcknowledged] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      /* clipboard denied — user can copy manually */
    }
  };

  const handleDownload = () => {
    const ts = new Date().toISOString().slice(0, 10);
    const body =
      `Matrica OMS — Two-Factor Authentication Backup Codes\n` +
      `Generated: ${new Date().toISOString()}\n\n` +
      `Each code can be used ONCE. Keep this file in a safe place.\n` +
      `If you lose access to your authenticator app, use one of these codes to sign in.\n\n` +
      codes.join('\n');
    const blob = new Blob([body], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `matrica-oms-backup-codes-${ts}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
      <div>
        <h4 className="text-sm font-semibold text-gray-900">Your backup codes</h4>
        <p className="text-xs text-gray-500 mt-1">
          Save these codes now — they will <strong>not be shown again</strong>. Each code works
          once and lets you sign in if you lose access to your authenticator app.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-gray-50 border border-gray-200 rounded p-3 select-all">
        {codes.map((c, i) => (
          <div key={i} className="px-2 py-1">
            {c}
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleCopy}
          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
        >
          {copied ? 'Copied!' : 'Copy all'}
        </button>
        <button
          type="button"
          onClick={handleDownload}
          className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md text-sm hover:bg-gray-50"
        >
          Download .txt
        </button>
      </div>

      <div className="border-t border-gray-200 pt-4">
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={(e) => setAcknowledged(e.target.checked)}
            className="mt-1"
          />
          <span>
            I have saved these codes in a safe place. I understand they cannot be retrieved
            again.
          </span>
        </label>
        <button
          type="button"
          onClick={onAcknowledge}
          disabled={!acknowledged}
          className="mt-3 w-full px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Done
        </button>
      </div>
    </div>
  );
}
